import React, { useEffect, useRef, useState } from "react";
import InputField from "./InputField";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import UserService from "rc-app/services/UserService";
import { CardBorder } from "../CardBorder";
import CustomSelect from "./CustomSelect";
import Icon from "../Icons";

interface ClientDetailsProps {
  formData: any;
  onInputChange: (e: any) => void;
  onBlur: (fieldName: string) => void;
  errors: any;
  touched: Set<string>;
  isEditMode?: boolean;
  [key: string]: any;
}

const ClientDetails: React.FC<ClientDetailsProps> = ({
  formData,
  onInputChange,
  onBlur,
  errors,
  touched,
  isEditMode = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setImageLoadError(false);
  }, [formData?.logo]);

  const handleLocalBlur = (e: any) => {
    const { name } = e.target;
    onBlur(name);
  };

  const shouldShowError = (fieldName: string) => {
    return touched.has(fieldName) && errors[fieldName];
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedImage = e.target.files?.[0];
    if (!selectedImage) return;

    setIsUploading(true);

    // Sanitize filename: spaces and special chars produce URLs that fail
    // yup.string().url() validation downstream, blocking the Next button.
    const dotIndex = selectedImage.name.lastIndexOf(".");
    const base = dotIndex > 0 ? selectedImage.name.slice(0, dotIndex) : selectedImage.name;
    const ext = dotIndex > 0 ? selectedImage.name.slice(dotIndex) : "";
    const safeName =
      base.replace(/[^a-zA-Z0-9._-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") + ext.toLowerCase();
    const uploadFile = new File([selectedImage], safeName, { type: selectedImage.type });

    const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/realestate/gc_upload`;
    const uploadFormData = new FormData();
    uploadFormData.append("gc_upload_request", uploadFile);

    try {
      const res = await axios.post(API_URL, uploadFormData, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "multipart/form-data",
        },
      });

      const cacheBustedUrl = `${res.data.uploaded_url}?t=${Date.now()}`;
      onInputChange({ target: { name: "logo", value: cacheBustedUrl } });
    } catch (error) {
      console.error("Image upload failed:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageDelete = () => {
    onInputChange({ target: { name: "logo", value: "" } });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="font-inter text-xl flex flex-col bg-[#1C2C3A]">
      <CardBorder title="Client Details" borderColor="border-[#2E4051]">
        <div className="flex w-full gap-4 items-center justify-center">
          <div className="flex flex-col max-md:w-1/2 max-xl:w-1/2 items-center ">
            <div className="flex space-x-4">
              <span className={`p-2 text-base font-normal text-[#889DB2]`}>
                Client's Logo
              </span>
            </div>
            <div className="relative mb-3">
              {!formData?.logo || imageLoadError ? (
                formData?.name && formData.name.length >= 2 ? (
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center bg-[#2E4051] text-[#889DB2] text-3xl md:text-4xl font-bold border-4 border-[#889DB2] shadow-md">
                    {`${formData.name[0]?.toUpperCase()}${formData.name[1]?.toUpperCase()}`}
                  </div>
                ) : (
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center bg-[#2E4051] text-[#889DB2] border-4 border-[#889DB2] shadow-md">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )
              ) : (
                <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-[#889DB2] shadow-md">
                  <img
                    src={formData.logo}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => setImageLoadError(true)}
                  />
                </div>
              )}
            </div>

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageChange}
            />

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={isUploading}
                className="bg-[#008564] text-white p-3 rounded-lg hover:bg-[#008564] hover:opacity-70 transition-colors disabled:opacity-50"
                title="Upload New Image"
              >
                {isUploading ? (
                  <CircularProgress size={20} style={{ color: "white" }} />
                ) : (
                  <>
                    {formData?.logo && (
                      <Icon name="ic-edit-img" className="w-4 h-4" />
                    )}
                    {!formData?.logo && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    )}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleImageDelete}
                disabled={isUploading || !formData?.logo}
                className="bg-[#2E4051] text-white p-3 rounded-lg hover:bg-[#008564] hover:opacity-70 transition-colors disabled:opacity-50"
                title="Delete Photo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>

            <div className="mt-4 w-full max-w-[240px] rounded-md bg-[#2E4051]/60 border border-[#3a4f63] px-3 py-2.5 text-[11px] text-[#B6C7DA] leading-relaxed">
              <p className="font-semibold text-white mb-1.5 text-[12px]">Logo upload specs</p>
              <ul className="space-y-0.5 list-disc list-inside marker:text-[#008564]">
                <li>SVG or transparent PNG</li>
                <li>Horizontal lockup, ratio ~3:1 to 4:1</li>
                <li>Min 800 × 200 px</li>
                <li>Must be readable on dark backgrounds</li>
              </ul>
            </div>
          </div>

          <div className="flex w-full flex-wrap">
            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Name "
                name="name"
                value={formData.name}
                onChange={onInputChange}
                onBlur={handleLocalBlur}
                placeholder="Enter client name"
                error={shouldShowError("name") ? errors.name : undefined}
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <CustomSelect
                label="Client Type"
                name="agency_type"
                value={formData.agency_type}
                onChange={onInputChange}
                onBlur={handleLocalBlur}
                error={
                  shouldShowError("agency_type") ? errors.agency_type : null
                }
                options={[
                  { value: "Developer", label: "Developer" },
                  { value: "Marketing", label: "Marketing" },
                ]}
                isRadio={true}
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Onboarded by "
                name="channel_partner"
                value={formData.channel_partner}
                onChange={onInputChange}
                onBlur={handleLocalBlur}
                placeholder="Enter name"
                error={
                  shouldShowError("channel_partner")
                    ? errors.channel_partner
                    : undefined
                }
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <CustomSelect
                label="Client Footprint"
                name="footprint"
                value={formData.footprint}
                onChange={onInputChange}
                onBlur={handleLocalBlur}
                error={shouldShowError("footprint") ? errors.footprint : null}
                options={[
                  { value: "Single State", label: "Single - State" },
                  { value: "Multi State", label: "Multi - State" },
                ]}
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Slug"
                name="slug"
                value={formData.slug}
                onChange={onInputChange}
                onBlur={handleLocalBlur}
                placeholder="Enter slug"
                error={
                  shouldShowError("slug")
                    ? errors.slug
                    : undefined
                }
              />
            </div>

          </div>
        </div>
      </CardBorder>

      <div className="flex flex-col mt-8 pt-6">
        <CardBorder title="Client Address" borderColor="border-[#2E4051]">
          {isEditMode ? (
            // Single address field for edit mode
            // <div className="flex flex-wrap -mx-3">
              <div className="flex w-full px-3 mb-6">
                <InputField
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter complete address"
                  error={
                    shouldShowError("address") ? errors["address"] : undefined
                  }
                />
              </div>
            // </div>
          ) : (
            // Multiple address fields for add mode
            <div className="flex flex-wrap -mx-3">
              <div className="flex w-full md:w-1/2 px-3 mb-6">
                <InputField
                  label="Address line 1"
                  name="address.addressLine1"
                  value={formData.address.addressLine1}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter address line 1"
                  error={
                    shouldShowError("address.addressLine1")
                      ? errors["address.addressLine1"]
                      : undefined
                  }
                />
              </div>

              <div className="flex w-full md:w-1/2 px-3 mb-6">
                <InputField
                  label="Address line 2"
                  name="address.addressLine2"
                  value={formData.address.addressLine2}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter address line 2"
                  error={
                    shouldShowError("address.addressLine2")
                      ? errors["address.addressLine2"]
                      : undefined
                  }
                />
              </div>

              <div className="flex w-full md:w-1/2 px-3 mb-6">
                <InputField
                  label="City"
                  name="address.city"
                  value={formData.address.city}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter city"
                  error={
                    shouldShowError("address.city")
                      ? errors["address.city"]
                      : undefined
                  }
                />
              </div>

              <div className="flex w-full md:w-1/2 px-3 mb-6">
                <InputField
                  label="Pincode"
                  name="address.pincode"
                  value={formData.address.pincode}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter 6-digit pincode"
                  type="text"
                  maxLength={6}
                  error={
                    shouldShowError("address.pincode")
                      ? errors["address.pincode"]
                      : undefined
                  }
                />
              </div>

              <div className="flex w-full md:w-1/2 px-3 mb-6">
                <InputField
                  label="Landmark"
                  name="address.landmark"
                  value={formData.address.landmark}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter landmark"
                  error={
                    shouldShowError("address.landmark")
                      ? errors["address.landmark"]
                      : undefined
                  }
                />
              </div>

              <div className="flex w-full md:w-1/2 px-3 mb-6">
                <InputField
                  label="State"
                  name="address.state"
                  value={formData.address.state}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter state"
                  error={
                    shouldShowError("address.state")
                      ? errors["address.state"]
                      : undefined
                  }
                />
              </div>

              <div className="flex w-full md:w-1/2 px-3 mb-6">
                <InputField
                  label="Country"
                  name="address.country"
                  value={formData.address.country}
                  onChange={onInputChange}
                  onBlur={handleLocalBlur}
                  placeholder="Enter country"
                  error={
                    shouldShowError("address.country")
                      ? errors["address.country"]
                      : undefined
                  }
                />
              </div>
            </div>
          )}
        </CardBorder>
      </div>
    </div>
  );
};

export default ClientDetails;
